import { type Booking } from "@/hooks/useBookings";
import { type Property } from "@/types/property";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useMemo } from "react";
import CalendarDaysHeaderVirtualized from "../../../../molecules/admin/tabs/AdminCalendarTab/CalendarDaysHeaderVirtualized";
import CalendarGridRows from "../../../../molecules/admin/tabs/AdminCalendarTab/CalendarGridRows";
import CalendarHeaderVirtualized from "../../../../molecules/admin/tabs/AdminCalendarTab/CalendarHeaderVirtualized";

interface MonthHeader {
  name: string;
  days: number;
  startIndex: number;
}

interface DayStatus {
  isPast: boolean;
  isBlocked: boolean;
  isBooked: boolean;
  canClick: boolean;
}
interface CalendarVirtualizedProps {
  calendarScrollRef: React.RefObject<HTMLDivElement>;
  properties: Property[];
  days: Date[];
  months: MonthHeader[];
  today: Date;
  cellWidth?: number;
  rowHeight?: number;
  bookingsByProperty: Record<string, Booking[]>;
  onOpenModal: (date: Date, property: Property) => void;
  onBookingClick?: (booking: Booking) => void;
  getDayStatus: (day: Date, propertyId: string) => DayStatus;
}

const CalendarVirtualized = ({
  calendarScrollRef,
  properties,
  days,
  months,
  today,
  cellWidth = 60,
  rowHeight = 70,
  onOpenModal,
  bookingsByProperty,
  onBookingClick,
  getDayStatus,
}: CalendarVirtualizedProps) => {
  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: days.length,
    getScrollElement: () => calendarScrollRef.current,
    estimateSize: () => cellWidth,
    overscan: 200,
  });

  const rowVirtualizer = useVirtualizer({
    count: properties.length,
    getScrollElement: () => calendarScrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 5,
  });

  const columnMonthVirtualizer = useVirtualizer({
    horizontal: true,
    count: months.length,
    getScrollElement: () => calendarScrollRef.current,
    getItemKey: (index) => months[index].name,
    estimateSize: (index) => months[index].days * cellWidth,
    overscan: 4,
  });

  const columnVirtualItems = columnVirtualizer.getVirtualItems();

  const visibleRange = useMemo(() => {
    const items = columnVirtualItems;
    if (!items.length) return { start: 0, end: 0 };
    return {
      start: items[0].start,
      end: items[items.length - 1].end,
    };
  }, [columnVirtualItems]);

  const handleDayClick = (day: Date, property: Property) => {
    const { canClick } = getDayStatus(day, property.id);

    if (canClick) onOpenModal(day, property);
  };

  return (
    <div
      ref={calendarScrollRef}
      className="overflow-hidden overflow-y-hidden flex-1"
    >
      <div
        style={{
          width: columnVirtualizer.getTotalSize(),
          height: rowVirtualizer.getTotalSize() + rowHeight * 2,
          position: "relative",
        }}
      >
        <CalendarHeaderVirtualized
          months={months}
          columnMonthVirtualizer={columnMonthVirtualizer}
          cellWidth={cellWidth}
          rowHeight={rowHeight}
        />

        <CalendarDaysHeaderVirtualized
          days={days}
          today={today}
          columnVirtualizer={columnVirtualizer}
          rowHeight={rowHeight}
        />

        <CalendarGridRows
          properties={properties}
          days={days}
          rowHeight={rowHeight}
          cellWidth={cellWidth}
          rowVirtualizer={rowVirtualizer}
          columnVirtualizer={columnVirtualizer}
          bookingsByProperty={bookingsByProperty}
          getDayStatus={getDayStatus}
          visibleRange={visibleRange}
          onDayClick={handleDayClick}
          onBookingClick={onBookingClick}
        />
      </div>
    </div>
  );
};

export default CalendarVirtualized;
